// src/layout/customer/Payment/CustomerProjectValueHistoryTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaEye } from "react-icons/fa";
import GenericModal from "../../../components/common/GenericModal"; // Assuming you have this
import useCustomerPaymentStore from "../../../store/customer/useCustomerPaymentStore";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { ArrowUp, ArrowDown, Check, X } from "lucide-react";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup";

const formatDisplayDate = (isoDateString) => {
  if (!isoDateString) return "N/A";
  try {
    return new Date(isoDateString)
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  } catch (e) {
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return parseFloat(amount).toLocaleString("en-IN");
};

const statusDisplayInfo = (status) => {
  switch (status) {
    case "Approved":
      return { text: "text-green-700", bg: "bg-green-100" };
    case "Pending":
      return { text: "text-yellow-700", bg: "bg-yellow-100" };
    case "Reject":
    case "Rejected": // Handle both possible values
      return { text: "text-red-700", bg: "bg-red-100" };
    default:
      return { text: "text-gray-700", bg: "bg-gray-100" };
  }
};

const CustomerProjectValueHistoryTable = ({ projectId }) => {
  const {
    projectValueEntries,
    isValueEntriesLoading,
    fetchProjectValueEntriesForCustomer,
    respondToValueEntry,
    isActionSubmitting,
  } = useCustomerPaymentStore();

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");
  const [confirmPopup, setConfirmPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectValueEntriesForCustomer(projectId);
    }
  }, [projectId, fetchProjectValueEntriesForCustomer]);

  const latestProjectValue = useMemo(() => {
    // Find the most recent entry that has been approved for the footer summary.
    const latestApprovedEntry = projectValueEntries.find(
      (entry) => entry.status === "Approved"
    );
    if (latestApprovedEntry) {
      return parseFloat(
        latestApprovedEntry.amount?.toString().replace(/,/g, "") || "0"
      );
    }
    return 0;
  }, [projectValueEntries]);

  const handleViewComment = (comment) => {
    setSelectedComment(comment);
    setIsCommentModalOpen(true);
  };

  const handleAction = (entry, action) => {
    const actionText = action.toLowerCase();
    setConfirmPopup({
      isOpen: true,
      title: `Confirm ${action}`,
      message: `Are you sure you want to ${actionText} this project value entry?`,
      onConfirm: async () => {
        await respondToValueEntry(
          entry.project_id,
          entry.id,
          action,
          `Value entry ${actionText} by customer.`
        );
        setConfirmPopup({ isOpen: false }); // Close popup after action
      },
    });
  };

  if (isValueEntriesLoading && projectValueEntries.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <AiOutlineLoading3Quarters className="animate-spin text-2xl text-blue-500" />
        <p className="ml-2 text-gray-500">Loading project value history...</p>
      </div>
    );
  }

  if (!isValueEntriesLoading && projectValueEntries.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500">
        No project value history found.
      </div>
    );
  }

  return (
    <>
      <div
        className="p-3 lg:p-4 bg-white shadow-md rounded-lg overflow-x-auto"
        style={{ borderRadius: "12px" }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h2 className="text-lg lg:text-xl font-semibold">Project Value History</h2>
        </div>

        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse text-xs min-w-[600px]">
            <thead>
              <tr className="text-left border-b border-gray-300 bg-gray-50">
                <th className="p-2 lg:p-2.5">Date Recorded</th>
                <th className="p-2 lg:p-2.5">Comments</th>
                <th className="p-2 lg:p-2.5 text-center">Status</th>
                <th className="p-2 lg:p-2.5 text-center">Change (INR)</th>
                <th className="p-2 lg:p-2.5 text-right">Project Value (INR)</th>
              </tr>
            </thead>
            <tbody>
              {projectValueEntries.map((project, index) => {
                const statusInfo = statusDisplayInfo(project.status);

                const previousApprovedEntry = projectValueEntries
                  .slice(index + 1)
                  .find((entry) => entry.status === "Approved");

                const isFirstApprovedEntry =
                  project.status === "Approved" && !previousApprovedEntry;

                let change = null;
                if (
                  project.status === "Approved" ||
                  project.status === "Pending"
                ) {
                  if (previousApprovedEntry) {
                    change = project.amount - previousApprovedEntry.amount;
                  } else {
                    change = project.amount;
                  }
                }

                return (
                  <tr
                    key={project.id}
                    className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 whitespace-nowrap text-xs">
                      {formatDisplayDate(project.entry_date)}
                    </td>
                    <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 max-w-[120px] lg:max-w-xs">
                      <div className="flex items-center">
                        <span className="truncate mr-1 lg:mr-2 text-xs">{project.comment}</span>
                        <button
                          onClick={() => handleViewComment(project.comment)}
                          className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                        >
                          <FaEye size={10} className="lg:w-3 lg:h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 text-center">
                      {project.status === "Pending" ? (
                        <div className="flex justify-center items-center gap-2">
                          <button
                            onClick={() => handleAction(project, "Approved")}
                            disabled={isActionSubmitting}
                            className="px-1.5 lg:px-2 py-0.5 lg:py-1 text-[9px] lg:text-[10px] bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors duration-200 flex items-center gap-0.5 lg:gap-1 disabled:opacity-60"
                          >
                            <Check size={10} className="lg:w-3 lg:h-3" /> Approve
                          </button>
                          <button
                            onClick={() => handleAction(project, "Reject")}
                            disabled={isActionSubmitting}
                            className="px-1.5 lg:px-2 py-0.5 lg:py-1 text-[9px] lg:text-[10px] bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors duration-200 flex items-center gap-0.5 lg:gap-1 disabled:opacity-60"
                          >
                            <X size={10} className="lg:w-3 lg:h-3" /> Reject
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`inline-block px-1.5 lg:px-2 py-0.5 lg:py-1 font-semibold rounded-full text-[9px] lg:text-[10px] ${statusInfo.bg} ${statusInfo.text}`}
                        >
                          {project.status || "N/A"}
                        </span>
                      )}
                    </td>
                    <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 text-center">
                      {isFirstApprovedEntry ? (
                        <span className="text-gray-500 font-medium text-xs">
                          Initial Value
                        </span>
                      ) : change !== null ? (
                        change > 0 ? (
                          <span className="text-green-600 font-semibold flex items-center justify-center gap-0.5 lg:gap-1 text-xs">
                            <ArrowUp size={10} className="lg:w-3 lg:h-3" /> + {formatCurrency(change)}
                          </span>
                        ) : change < 0 ? (
                          <span className="text-red-600 font-semibold flex items-center justify-center gap-0.5 lg:gap-1 text-xs">
                            <ArrowDown size={10} className="lg:w-3 lg:h-3" /> - {formatCurrency(Math.abs(change))}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-xs">-</span>
                        )
                      ) : (
                        <span className="text-gray-500 text-xs italic">
                          (No Change)
                        </span>
                      )}
                    </td>
                    <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 whitespace-nowrap text-gray-800 text-right font-medium text-xs">
                      {formatCurrency(project.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-bold border-t-2 border-gray-300 bg-gray-50">
                <td colSpan="4" className="p-2 lg:p-2.5 text-right text-sm lg:text-base">
                  Latest Approved Project Value
                </td>
                <td className="p-2 lg:p-2.5 text-gray-800 text-right text-sm lg:text-base font-bold">
                  {formatCurrency(latestProjectValue)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile Card View - Shown only on mobile */}
        <div className="md:hidden space-y-3">
          {projectValueEntries.map((project, index) => {
            const statusInfo = statusDisplayInfo(project.status);

            const previousApprovedEntry = projectValueEntries
              .slice(index + 1)
              .find((entry) => entry.status === "Approved");

            const isFirstApprovedEntry =
              project.status === "Approved" && !previousApprovedEntry;

            let change = null;
            if (
              project.status === "Approved" ||
              project.status === "Pending"
            ) {
              if (previousApprovedEntry) {
                change = project.amount - previousApprovedEntry.amount;
              } else {
                change = project.amount;
              }
            }

            return (
              <div
                key={project.id}
                className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
              >
                {/* Date */}
                <div className="flex justify-between items-start mb-2">
                  <div className="text-xs text-gray-600">
                    {formatDisplayDate(project.entry_date)}
                  </div>
                  {project.status !== "Pending" && (
                    <span
                      className={`inline-block px-2 py-0.5 font-semibold rounded-full text-[9px] ${statusInfo.bg} ${statusInfo.text}`}
                    >
                      {project.status || "N/A"}
                    </span>
                  )}
                </div>

                {/* Comment */}
                <div className="mb-2">
                  <div className="flex items-start justify-between">
                    <span className="text-xs text-gray-800 flex-1 mr-2">
                      {project.comment}
                    </span>
                    <button
                      onClick={() => handleViewComment(project.comment)}
                      className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                    >
                      <FaEye size={12} />
                    </button>
                  </div>
                </div>

                {/* Pending Actions */}
                {project.status === "Pending" && (
                  <div className="mb-2 flex gap-2">
                    <button
                      onClick={() => handleAction(project, "Approved")}
                      disabled={isActionSubmitting}
                      className="flex-1 px-3 py-1.5 text-xs bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors duration-200 flex items-center justify-center gap-1 disabled:opacity-60"
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => handleAction(project, "Reject")}
                      disabled={isActionSubmitting}
                      className="flex-1 px-3 py-1.5 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors duration-200 flex items-center justify-center gap-1 disabled:opacity-60"
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}

                {/* Change & Value */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <div className="text-xs">
                    <span className="text-gray-500 block mb-1">Change:</span>
                    {isFirstApprovedEntry ? (
                      <span className="text-gray-500 font-medium">
                        Initial Value
                      </span>
                    ) : change !== null ? (
                      change > 0 ? (
                        <span className="text-green-600 font-semibold flex items-center gap-0.5">
                          <ArrowUp size={10} /> + {formatCurrency(change)}
                        </span>
                      ) : change < 0 ? (
                        <span className="text-red-600 font-semibold flex items-center gap-0.5">
                          <ArrowDown size={10} /> - {formatCurrency(Math.abs(change))}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )
                    ) : (
                      <span className="text-gray-500 italic text-xs">
                        (No Change)
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-gray-500 block mb-1 text-xs">Value:</span>
                    <span className="font-semibold text-gray-900 text-sm">
                      ₹{formatCurrency(project.amount)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Latest Value Footer for Mobile */}
          <div className="bg-gray-50 rounded-lg border-2 border-gray-300 p-3 font-bold">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Latest Approved Project Value</span>
              <span className="text-sm text-gray-800">
                ₹{formatCurrency(latestProjectValue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <GenericModal
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        title="Full Comment"
      >
        <p className="whitespace-pre-wrap break-words">{selectedComment}</p>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setIsCommentModalOpen(false)}
            className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs font-medium"
          >
            Close
          </button>
        </div>
      </GenericModal>

      <ConfirmationPopup
        isOpen={confirmPopup.isOpen}
        onClose={() => setConfirmPopup({ isOpen: false })}
        onConfirm={confirmPopup.onConfirm}
        title={confirmPopup.title}
        message={confirmPopup.message}
        isLoading={isActionSubmitting}
      />
    </>
  );
};

export default CustomerProjectValueHistoryTable;
